/**
 * All gameplay tuning lives here (PRD §6: "Movement parameters must be data-driven
 * and easily tunable"). Units are metres and seconds.
 *
 * Nothing in here imports anything else in `game/` — every other module is free
 * to read these tables without creating a cycle.
 */

export const WORLD_SEED = 20260812;

export const SUB = {
  /**
   * Hull half-length; the collision proxy is three spheres along this axis.
   *
   * 7.2, up from 6.2. The CAD hull carries far more detail than the scan it
   * replaced and at the old size none of it was legible — 129 px of boat on a
   * 1920 px frame, which is not enough pixels to show a deck fitting.
   *
   * The ceiling is the DRAY, at 7.6. It is the heavy hauler and it has to stay
   * the bigger boat, and its mesh states its own dimensions throughout
   * `buildDray()` rather than deriving them, so lifting the whole fleet is a
   * separate job. 7.2 is as far as the Pathfinder goes without inverting the
   * two. The rest of the gain comes from the camera; see CAMERA.viewHeight.
   *
   * `HULL_LEN` in src/render/models.ts and in both bake scripts must match.
   */
  length: 7.2,
  /**
   * Collision radius, and it FOLLOWS THE SILHOUETTE.
   *
   * 1.55 was sized for the old hull, which peaked at 1.12 and carried nacelles
   * out to |z| 1.6. The retuned hull peaks at 0.75 with pods at |z| 0.86, so
   * keeping 1.55 would have meant a boat that collides with rock it visibly
   * clears — the exact complaint that has come back twice already, and this
   * time it would have been self-inflicted.
   *
   * 1.18 rather than 0.9: the sail still stands 1.55 above the axis and the
   * three proxy spheres are placed along the hull, so the envelope has to cover
   * the tower as well as the tube. Deliberately still a little generous — a sub
   * that scrapes slightly early is forgiving, one that clips late is a bug.
   *
   * This MOVES the physics, so `scripts/golden-trace.json` was re-baselined
   * with `node scripts/trace.mjs --write`. That is the one legitimate reason to
   * regenerate it: the goldens exist to prove a refactor changed nothing, and a
   * deliberate change to the collision envelope is not a refactor.
   *
   * 1.37 now rather than 1.18, which is 1.18 x 7.2/6.2 exactly: the hull grew
   * and the envelope grew with it, in step. Every proxy below is a multiple of
   * this and of `length`, so the whole collision solid scales uniformly and the
   * measurements the ratios came from stay true. Re-baselined again, for the
   * same reason and by the same command.
   */
  radius: 1.37,

  thrustX: 62,
  thrustY: 46,
  /** Linear drag; terminal speed = thrust / drag. */
  dragX: 4.1,
  dragY: 5.0,
  /** Extra drag applied when there is no input — the "responsive braking" of PRD §2.2. */
  brakeBoost: 2.6,

  /** Mild positive buoyancy so the sub drifts up when idle. */
  buoyancy: 3.1,

  /** Base capacities. Upgrades raise these — see `upgrades.ts`. */
  maxHull: 100,
  maxEnergy: 100,
  energyRegen: 13,

  /** Visual-only attitude response. */
  bankMax: 0.62,
  pitchMax: 0.5,
  attitudeRate: 6.0,
  yawRate: 7.5,

  /** Terrain contact. */
  bounce: 0.28,
  scrapeFriction: 0.55,
  /** Impact speed below this does no damage at all (PRD §7: gentle contact must not punish). */
  safeImpactSpeed: 9.0,
  impactDamage: 1.1,

  /**
   * Mercy rules. A hit buys a slice of invulnerability so overlapping threats
   * cannot chain-delete the sub, and a respawn buys much more so you are never
   * killed before you have your bearings.
   */
  hitInvuln: 0.55,
  respawnInvuln: 3.2,
  startLives: 3,
} as const;

/**
 * Shields sit in front of the hull: they soak damage first, and they come back
 * on their own a few seconds after the shooting stops. This is the main reason
 * a mistake is survivable — hull loss is only what got through.
 */
export const SHIELD = {
  base: 45,
  perLevel: 32,
  /** Points per second once recharging. */
  regen: 11,
  regenPerLevel: 4.5,
  /** Quiet seconds required before the recharge starts. */
  delay: 3.6,
  delayPerLevel: -0.45,
} as const;

/**
 * Nitro: a consumable burst of speed, not a stamina bar. It roughly doubles
 * cruise, drains fast, trickles back slowly, and is topped up by pickups — so
 * it reads as a resource you spend on a decision, not a button you hold.
 */
export const NITRO = {
  max: 100,
  /** Multiplier on thrust while burning. */
  thrust: 2.15,
  /** Drag multiplier while burning; the two together set the new terminal speed. */
  drag: 0.72,
  /** Units per second while burning. */
  drain: 30,
  /** Passive trickle, so you are never permanently grounded. */
  regen: 3.4,
  /** Cannot re-light below this — stops stutter-tapping for free speed. */
  minToStart: 12,
  /** Awarded by a nitro canister. */
  refill: 55,
} as const;

/**
 * Leaving the water, which the boat could not do until now.
 *
 * The surface used to be a wall — `y = surfaceY - 2.2` with a bounce — so the
 * sky the game had just learned to draw was permanently two metres out of
 * reach. It is a boundary the hull crosses instead, and everything below is
 * what changes about the world on the dry side of it.
 *
 * Nothing here is a special case for the player. The submerged path reduces to
 * exactly the arithmetic it replaced, because `air` is zero underwater and the
 * factors are literal 1.0s — which is what keeps `npm run trace` honest and
 * the whole balance table untouched.
 */
export const BREACH = {
  /**
   * m/s², real. The boat's terminal climb rate is 9.2, so a full-power exit
   * arcs a little over four metres and hangs for about two seconds — enough to
   * be a manoeuvre and not so much that the sea stops being the game.
   */
  gravity: 9.8,
  /**
   * How much of the thrust term the air takes away, at full extension.
   *
   * A screw in air has nothing to push and a dive plane in air has nothing to
   * bite. This is also, for free, the answer to "can you fly on nitro": the
   * multiplier is on a thrust term that has already gone to almost nothing, so
   * holding the burn through a breach buys height it cannot keep. No clamp, no
   * special case, nothing to forget.
   */
  thrustLoss: 0.92,
  /** Air is ~800x thinner than water. Drag written for a fluid that resists. */
  dragLoss: 0.94,
  /**
   * Metres above the waterline the hull is not allowed past.
   *
   * Gravity settles every normal arc long before this. It is here so a bad
   * impulse — a mine under the keel, a dreadnought's shove — cannot launch the
   * boat out of the world and strand it where the camera has nothing to frame.
   */
  ceiling: 26,
  /** Crossing speed, m/s, at which a splash is worth spawning at all. */
  minSplash: 1.6,
} as const;

/**
 * THE SEA STATE, and the CPU's copy of it.
 *
 * ONE COPY, and the shader reads it rather than repeating it.
 *
 * These used to exist twice — here and as constants in `post.wgsl` — with a
 * lint rule comparing them, because a silent drift would be nasty in a specific
 * way: the boat floating at a waterline nobody can see, bobbing against a swell
 * drawn somewhere else. Two copies plus a rule is a worse answer than one copy,
 * and it stopped being tenable the moment these became tunable: a slider cannot
 * move a WGSL constant, and a slider that moved only the picture would put the
 * hull on a different sea by design.
 *
 * So it is mutable, the renderer pushes it into the uniform every frame, and
 * `seaHeight` below reads the same object. Physics and picture cannot disagree
 * because there is nothing left to disagree with.
 */
export const SEA: {
  k: number; amp: number; speed: number; chop: number; octaves: number;
  fall: number; mul: number; legacyProfile: boolean;
} = {
  /** Radians per metre. 0.24 is a 26 m wind swell. */
  k: 0.24,
  amp: 1.05,
  /** Phase speed is `speed / k`, so 0.75 is about 3.1 m/s. */
  speed: 0.75,
  /** How hard the crests are pinched. 1 is a sine, 2.55 is lively wind chop. */
  chop: 2.55,
  /**
   * HEIGHT octaves, and this number is shared with the shader rather than
   * scaled by the graphics tier. Shading octaves may vary with the setting —
   * they only change how the sea is lit — but the height must not, or the boat
   * floats a different distance out of the water on LOW than on ULTRA and the
   * game plays differently at different settings. `WAVE_OCT_H` in post.wgsl is
   * the same number and rule 6 of the WGSL lint keeps them equal.
   */
  octaves: 2,
  /**
   * THE SPECTRUM'S SHAPE, and the pair that decides whether this is an ocean or
   * a texture. `fall` is how fast each octave loses amplitude, `mul` how fast it
   * gains frequency — and their PRODUCT is what matters: at 1.0 every octave
   * contributes the same slope and the finest one owns the surface, which is
   * how the earlier version came out looking like quilted leather. Under 1 the
   * swell leads and the chop rides it.
   */
  fall: 0.36,
  mul: 1.84,
  /** The older folded, domain-warped crest silhouette. */
  legacyProfile: true,
};

/**
 * Height of the sea above its still level at a world xz, metres.
 *
 * The exact profile selected by `waveH` in the composite: folded/domain-warped
 * or directional Stokes. This is what the hull floats on, so the two paths have
 * to be the same sea and not merely a similar one.
 */
export function seaHeight(x: number, z: number, t: number): number {
  let qx = x, qz = z;
  let k = SEA.k, amp = SEA.amp, chop = SEA.chop, h = 0;
  for (let i = 0; i < SEA.octaves; i++) {
    if (SEA.legacyProfile) {
      // The original profile: three folded waves through a slow domain warp.
      // It gives the cross-section pinched crests, broad troughs and irregular
      // groups instead of the graph-like lobes of a low-order Stokes sum.
      const w = Math.sin(qz * k * 0.271 + t * 0.19)
              + Math.sin(qx * k * 0.173 - t * 0.11) * 0.8;
      const sx = qx + (w * 1.15) / k * 0.35;
      const sz = qz + (w * -0.75) / k * 0.35;
      const a = 1 - Math.abs(Math.sin((sx * 0.94 + sz * 0.34) * k + t * SEA.speed));
      const b = 1 - Math.abs(Math.sin((sx * -0.42 + sz * 0.91) * k * 1.31
                                      - t * SEA.speed * 0.77 + 2.1));
      const c = 1 - Math.abs(Math.sin((sx * 0.71 - sz * 0.70) * k * 0.83
                                      + t * SEA.speed * 1.29 + 4.7));
      h += ((((a + b + c) / 3) ** chop) - 0.135) * amp;
      qx += h * 0.35;
      qz += h * 0.21;
      const rx = qx * 0.5403 - qz * 0.8415;
      qz = qx * 0.8415 + qz * 0.5403;
      qx = rx;
      k *= SEA.mul;
      amp *= SEA.fall;
      chop += (1 - chop) * 0.28;
      continue;
    }
    const sharp = Math.min(0.72, Math.max(0, (chop - 1) * 0.16));
    const p0 = (qx * 0.94 + qz * 0.34) * k + t * SEA.speed;
    const p1 = (qx * 0.70 + qz * 0.71) * k * 1.23
             + t * SEA.speed * 1.17 + 2.1;
    const p2 = (qx * 0.99 - qz * 0.13) * k * 1.73
             + t * SEA.speed * 1.36 + 4.7;
    const c0 = Math.cos(p0), c1 = Math.cos(p1), c2 = Math.cos(p2);
    // Second-order Stokes term: sharper crests, broader troughs, zero mean.
    const a = c0 + sharp * 0.22 * (2 * c0 * c0 - 1);
    const b = c1 + sharp * 0.22 * (2 * c1 * c1 - 1);
    const c = c2 + sharp * 0.22 * (2 * c2 * c2 - 1);
    // Products of the same three components create irregular wave groups and
    // occasional reinforced peaks without another trigonometric evaluation.
    // This is the missing cross-section variation: still directional, but no
    // longer a tidy stack of evenly interesting sine lobes.
    const group = c0 * c1 * 0.16 + c1 * c2 * 0.08;
    h += (a * 0.52 + b * 0.27 + c * 0.15 + group) * amp;
    // A small windward advection supplies the horizontal Gerstner cue while
    // keeping this a single-valued height field for collision and ray solving.
    qx += h * sharp * 0.08 * 0.94;
    qz += h * sharp * 0.08 * 0.34;
    const rx = qx * 0.9323 - qz * 0.3616;
    qz = qx * 0.3616 + qz * 0.9323;
    qx = rx;
    k *= SEA.mul;
    amp *= SEA.fall;
    chop += (1 - chop) * 0.28;
  }
  return h;
}

export const CAMERA = {
  fov: (42 * Math.PI) / 180,
  /**
   * Vertical extent of the gameplay plane, in metres.
   *
   * 38, down from 52, to make the boat read bigger.
   *
   * The trade is field of view, the same one `touchZoom` already made and for
   * the same reason. Taken together with the hull growing to 7.2 m, the boat
   * goes from 129 px on a 1920 px frame to 205 px -- 1.6x, which is the
   * difference between a silhouette and something with fittings on it -- and
   * you see 27% less water across.
   *
   * This is the cheap half of that gain: it moves NO clearance anywhere and
   * needs no golden re-baseline, where growing the boat does both. If the
   * framing turns out too tight in caves, take it back here first.
   */
  viewHeight: 38,
  lookAheadX: 16,
  lookAheadY: 5,
  followX: 4.2,
  /** Vertical follow is deliberately slower than horizontal (PRD §4). */
  followY: 2.3,
  lookAheadRate: 1.6,
  shakeDecay: 4.5,
  /** Extra zoom-out while the nitro is lit, so the speed has somewhere to go. */
  nitroZoom: 1.28,
  /**
   * Base framing on a touch device. BELOW ONE — this magnifies.
   *
   * `zoom` scales the metres of water on screen, so larger shows MORE and makes
   * everything smaller. It was set above one on the reasoning that a small
   * screen needs more warning; the boat then became a speck on a phone held at
   * arm's length, which is the opposite trade. Legibility of the thing you are
   * flying beats field of view when the display is six inches wide.
   */
  touchZoom: 0.87,
  /**
   * How far the frame opens at this hull's terminal speed, before the burner.
   *
   * Squared against speed, so pottering about is framed tightly and only real
   * pace widens it — and it is continuous, where tying the widening to nitro
   * alone made it a step change on a keypress.
   */
  speedZoom: 1.10,
  /**
   * Multiplier on the horizontal look-ahead at full nitro.
   *
   * The lead already scales with velocity, but terminal speed is terminal
   * whether you got there on the drive or on the burners — so lighting the
   * nitro moved the boat faster without showing any more of what it was about
   * to hit. At speed the picture should be further ahead of you, not the same
   * picture arriving sooner.
   */
  nitroLead: 2.1,
} as const;

export const WORLD = {
  /** Chunk width in metres (PRD §10). */
  chunkSize: 48,
  /** Marching-squares cell size for terrain meshing. */
  cell: 0.62,
  /** Vertical band that gets meshed. Deep enough for the abyssal zones. */
  yMin: -2900,
  yMax: 6,
  /** Chunks kept resident either side of the camera. */
  radius: 4,
  surfaceY: 0,
  /** Eastern end of authored content; the world stops being interesting past it. */
  /**
   * Western edge of the authored world. The descent runs east to `endX`; the
   * coral garden and the seagrass meadow run west to here, and the headland at
   * -2920 seals the map beyond it.
   *
   * Anything that maps a world x onto a bar or a chart needs BOTH ends. The
   * chart used `wx / endX` clamped to 0..1, so every metre west of the spawn
   * collapsed onto the left edge — press TAB out at the reef and the map showed
   * you sitting at the start of the descent, which is exactly what it looked
   * like.
   */
  startX: -3000,
  endX: 7900,
} as const;

export interface DepthBand {
  depth: number;
  name: string;
  /** Per-channel extinction — red dies first, which is what sells "underwater". */
  extinction: [number, number, number];
  fog: [number, number, number];
  ambient: [number, number, number];
  ambientLevel: number;
}

/**
 * Depth progression from PRD §9. Interpolated per-frame by depth.
 *
 * Extinction was raised by a third across every band. It buys three things at
 * once and they are all the same thing: distance separation for the background
 * layers, a lamp beam that stops reaching and starts pooling, and point lights
 * that fall off before they can flood a room — `spot` and `points` both carry
 * an `exp(-ext · d)` travel term, so thicker water localises light for free.
 * Under-absorbed water is the single thing that most makes a rendered ocean
 * read as a fogged room instead of an ocean.
 */
export const BANDS: DepthBand[] = [
  // THE TOP THREE BANDS ARE BLUE NOW, NOT GREEN, AND THE REASON IS THE RATIO.
  //
  // What makes clear water blue is not its inscatter colour, it is that blue is
  // absorbed far less than everything else — so blue is what survives to be
  // scattered back at you. Clear seawater runs about 12 : 3 : 1 across red,
  // green and blue. These bands ran 3.8 : 1.4 : 1, where green survives almost
  // as well as blue does, and water in which green survives is a river.
  //
  // 6.8 : 2.0 : 1 now — short of the real figure, because at 12 : 3 : 1 red
  // dies inside a few metres and takes the hull's markings and every warm light
  // with it, which is true and unplayable. Blue reaches a third further than it
  // did, and the inscatter follows the same logic: blue clearly ahead of green
  // rather than level with it.
  //
  // Green sits a little higher than the first pass put it. Pushing blue in
  // front was the whole point, but doing it by taking green away as well
  // subtracted twice — the turquoise a shallow sea is actually made of is green
  // UNDER blue, not blue on its own, and at 2.2 it was draining out of the
  // middle distance faster than the eye reads as water.
  //
  // Only the top three. Below about four hundred metres there is no red or
  // green left to argue about and those bands were already blue.
  { depth: 0,    name: 'COASTAL SHELF',     extinction: [0.0392, 0.01175, 0.00580], fog: [0.042, 0.276, 0.430], ambient: [0.34, 0.79, 0.97], ambientLevel: 0.90 },
  { depth: 90,   name: 'COASTAL SHELF',     extinction: [0.0448, 0.01395, 0.00680], fog: [0.020, 0.169, 0.290], ambient: [0.22, 0.62, 0.86], ambientLevel: 0.52 },
  { depth: 240,  name: 'KELP FOREST',       extinction: [0.0470, 0.01465, 0.00790], fog: [0.010, 0.100, 0.168], ambient: [0.15, 0.49, 0.70], ambientLevel: 0.30 },
  { depth: 400,  name: 'SUNKEN CITY',       extinction: [0.0442, 0.01635, 0.01072], fog: [0.008, 0.062, 0.108], ambient: [0.11, 0.34, 0.54], ambientLevel: 0.165 },
  { depth: 640,  name: 'MILITARY ZONE',     extinction: [0.0509, 0.01876, 0.01179], fog: [0.004, 0.036, 0.072], ambient: [0.07, 0.24, 0.42], ambientLevel: 0.095 },
  { depth: 1000, name: 'THERMAL VENTS',     extinction: [0.0563, 0.02010, 0.01233], fog: [0.010, 0.024, 0.040], ambient: [0.10, 0.15, 0.25], ambientLevel: 0.055 },
  { depth: 1400, name: 'THERMAL VENTS',     extinction: [0.0590, 0.02224, 0.01340], fog: [0.006, 0.014, 0.028], ambient: [0.07, 0.11, 0.20], ambientLevel: 0.032 },
  { depth: 1800, name: 'THE ABYSS',         extinction: [0.0643, 0.02492, 0.01447], fog: [0.0006, 0.006, 0.016], ambient: [0.014, 0.055, 0.125], ambientLevel: 0.016 },
  { depth: 2400, name: 'ANCIENT STRUCTURE', extinction: [0.0670, 0.02626, 0.01514], fog: [0.0004, 0.0035, 0.011], ambient: [0.010, 0.038, 0.098], ambientLevel: 0.009 },
  { depth: 2900, name: 'ANCIENT STRUCTURE', extinction: [0.0697, 0.02760, 0.01581], fog: [0.0002, 0.002, 0.007], ambient: [0.006, 0.026, 0.070], ambientLevel: 0.006 },
];

export const LIGHT = {
  /**
   * 68, not 92. A 92 m beam out of a hand-sized reflector was reaching most of
   * the way across the screen, which made the lamp read as area lighting for
   * the whole scene rather than as a tool pointed at one thing. Shorter is also
   * the only way the beam ever has an END the player can see.
   */
  searchRange: 68,
  /**
   * 6 and 16 degrees, down from 10 and 23.
   *
   * 23 degrees is a floodlight. A diving lamp is a reflector behind a lens and
   * throws something much tighter — and a tight beam is worth far more here,
   * because a narrow pool of light that must be AIMED is the thing that makes
   * the dark feel dark. A wide one just raises the exposure.
   */
  searchInner: Math.cos((6 * Math.PI) / 180),
  searchOuter: Math.cos((16 * Math.PI) / 180),
  /** Must match searchOuter — the shadow fan spans the full outer cone. */
  searchHalfAngle: (16 * Math.PI) / 180,
  /**
   * Raised with the falloff below, which costs about 2.4x at the ranges the
   * player actually reads the beam at, and because a narrower cone concentrates
   * the same flux. Held to 12 rather than matched exactly: the point is to
   * reshape the beam, not to blow out the rock three metres off the bow.
   */
  searchIntensity: 12.0,
  /**
   * Angular resolution of the lamp's shadow. The beam is a cone in the XY
   * plane, so its occlusion is one-dimensional — a handful of rays from the
   * lamp is the whole shadow, which is why this costs almost nothing.
   */
  //
  // 64, NOT 96, BECAUSE THE CONE GOT NARROWER AND THIS DID NOT FOLLOW.
  //
  // What matters is degrees per ray, not rays. 96 across a 46-degree cone was
  // 0.48 degrees each; the beam is 32 degrees now, so the same 96 rays are
  // resolving it 30 percent finer than it ever needed — paying for detail
  // nobody asked for, in the most expensive CPU loop in the frame. 64 across 32
  // degrees is 0.50 each, which is what the beam was always shaded at.
  //
  // It is the single biggest CPU item there is: the F3 readout puts `lamp` at
  // 2.4 ms of a 9 ms total, and `frame` at 2.55 — meaning building the frame is
  // very nearly this and nothing else.
  shadowBins: 64,
  /**
   * Coarse stride for the beam march, refined by four bisections on a hit.
   * Measured at 4.6: no faster. The march is dominated by the noise inside
   * `density`, not by how many times it is called along a ray, so a longer
   * stride only buys the chance of stepping over thin rock.
   */
  shadowStep: 3.2,
  searchColor: [1.0, 0.94, 0.78] as [number, number, number],

  /**
   * One bounce of the lamp off whatever it is pointing at. The shadow march
   * already solves the distance to rock in every direction, so the lit
   * surfaces are known for free — this just asks them to give some of it back.
   * Sectors, not bins: 96 lights on one wall would eat the entire budget.
   */
  bounceSectors: 5,
  bounceRange: 26,
  bounceGain: 0.028,
  /** Wet rock, not lamp light: browner and far less saturated. */
  bounceColor: [1.0, 0.82, 0.58] as [number, number, number],
} as const;

// ---------------------------------------------------------------------------
// Weapons
// ---------------------------------------------------------------------------

/**
 * One entry per cannon level. Level 0 is the stock gun; each purchase widens
 * the pattern and steepens the rate, so the upgrade is legible from the first
 * shot rather than being a number on a menu.
 */
export interface CannonLevel {
  shots: number;
  /** Half-angle of the fan, radians. */
  spread: number;
  damage: number;
  /** Cooldown multiplier — lower is faster. */
  rate: number;
  energy: number;
  name: string;
}

export const CANNON_LEVELS: CannonLevel[] = [
  { shots: 1, spread: 0,     damage: 1.00, rate: 1.00, energy: 1.00, name: 'PULSE CANNON' },
  { shots: 1, spread: 0,     damage: 1.30, rate: 0.84, energy: 1.00, name: 'PULSE CANNON II' },
  { shots: 2, spread: 0.045, damage: 1.30, rate: 0.84, energy: 1.20, name: 'TWIN CANNON' },
  { shots: 3, spread: 0.105, damage: 1.45, rate: 0.78, energy: 1.40, name: 'SPREAD ARRAY' },
  { shots: 5, spread: 0.185, damage: 1.60, rate: 0.70, energy: 1.65, name: 'STORM ARRAY' },
  // The last two are what a maxed gun should feel like: the spread tightens
  // again rather than widening further, because a wall of shot that cannot be
  // aimed is a downgrade dressed as an upgrade.
  { shots: 5, spread: 0.145, damage: 1.95, rate: 0.60, energy: 1.70, name: 'TEMPEST ARRAY' },
  { shots: 7, spread: 0.165, damage: 2.25, rate: 0.52, energy: 1.85, name: 'MAELSTROM ARRAY' },
];

export const WEAPONS = {
  cannon: {
    speed: 105,
    damage: 9,
    cooldown: 0.14,
    energy: 3.0,
    /**
     * Seconds, and therefore RANGE: 105 m/s times this.
     *
     * It was 1.6, which is 168 m of reach. Measured against what a player can
     * actually see: in Survival the boat is fenced to x 400, the camera carries
     * a bias of 20-36 m toward the threat and opens to 1.3 zoom in a fight, so
     * forward visibility runs 78 m at rest to 111 m at its widest. The gun
     * outranged the screen by half again to more than double — which arrives as
     * a horde that dies before it arrives, and it is exactly the complaint.
     *
     * 1.0 puts the reach at 105 m: you can shoot to the edge of the frame when
     * the fight has opened it up, and no further.
     *
     * It is also the hottest loop in the mode. A STORM ARRAY fires five rounds
     * every 0.098 s, so at 1.6 s of life there were about 82 bullets in the
     * water, and every one is tested against every enemy — 82 x 130 is 10,660
     * distance checks a frame. This takes a third of them off.
     */
    life: 1.0,
    radius: 0.42,
    /**
     * Ricochets before a round gives up.
     *
     * Two, not one: a single skip reads as a misfire, and two is enough to be
     * a shot somebody aimed round a corner on purpose. Each costs 14 percent
     * of the speed and 38 percent of the damage, so a bounce is a flourish
     * rather than a way to farm walls — and only shallow hits skip at all, so
     * the ordinary shot into a wall still just stops.
     */
    bounces: 2,
  },
  torpedo: {
    speed: 42,
    accel: 30,
    maxSpeed: 66,
    damage: 55,
    turnRate: 2.6,
    cooldown: 0.85,
    life: 7.0,
    blastRadius: 11,
    radius: 0.8,
    maxAmmo: 12,
    /** Metres of rock a detonation carves out. 0 disables cratering. */
    carve: 7.5,
  },
  /** Depth charge: dropped, sinks, and detonates on rock or a timer. */
  charge: {
    damage: 78,
    blastRadius: 17,
    sink: 17,
    life: 4.2,
    radius: 1.0,
    cooldown: 1.15,
    maxAmmo: 8,
    carve: 11,
  },
  lock: {
    /** Seconds of sustained aim to reach 100 %. */
    time: 0.85,
    range: 140,
    /** Half-angle of the lock cone, radians. */
    cone: (55 * Math.PI) / 180,
    decay: 1.8,
  },
} as const;

/**
 * Timed power states. These are the loud, obvious pickups — the ones a seven
 * year old grabs on purpose.
 */
export const POWER = {
  overdrive: {
    time: 18,
    /** Cooldown and damage multipliers while lit. */
    rate: 0.45,
    damage: 1.75,
    /** Energy is free during an overdrive. */
    energy: 0,
  },
  /** Temporary invulnerable bubble. */
  aegis: { time: 9 },
} as const;

// ---------------------------------------------------------------------------
// Sonar
// ---------------------------------------------------------------------------

export const SONAR = {
  /** Metres per second the active ping travels (PRD §15 — it physically propagates). */
  speed: 190,
  maxRadius: 340,
  cost: 22,
  cooldown: 1.1,
  /** How long a revealed contact stays painted on the HUD. */
  contactLife: 6.5,
  /** Enemies within this multiple of the ping radius notice you pinging. */
  noiseRadius: 260,
  /**
   * Once the sonar array is upgraded the ping carries a concussive front: it
   * damages and shoves whatever the shell passes through. Turns the "look
   * around" button into a panic button, which is exactly when you want one.
   */
  pulseLevel: 2,
  pulseDamage: 26,
  pulseKnockback: 34,
  /** Radius of the concussive front, metres. */
  pulseRadius: 62,
} as const;

// ---------------------------------------------------------------------------
// Enemies
// ---------------------------------------------------------------------------

export type EnemyType =
  | 'mine' | 'drone' | 'jelly' | 'bloom'
  | 'seeker' | 'turret' | 'hunter' | 'swarmer' | 'lurker' | 'sentinel'
  | 'dreadnought';

export interface EnemyStats {
  hp: number;
  /** Collision / hit radius. Organics scale this by their rendered size. */
  radius: number;
  score: number;
  /** Salvage awarded on death, before any multiplier. */
  salvage: number;
  /** Damage per second while in contact with the hull. */
  contact: number;
  /** Drives the music mix and the "danger" read on the HUD. */
  threat: number;
  /** Lockable and worth a torpedo. Swarms and mines are not. */
  lockable: boolean;
}

export const ENEMY: Record<EnemyType, EnemyStats> = {
  mine:        { hp: 12,   radius: 1.9,  score: 60,   salvage: 4,   contact: 0,   threat: 0.25, lockable: false },
  seeker:      { hp: 22,   radius: 2.0,  score: 140,  salvage: 9,   contact: 0,   threat: 0.45, lockable: true  },
  drone:       { hp: 34,   radius: 2.1,  score: 150,  salvage: 10,  contact: 16,  threat: 0.5,  lockable: true  },
  jelly:       { hp: 16,   radius: 3.0,  score: 90,   salvage: 3,   contact: 5,   threat: 0.1,  lockable: false },
  bloom:       { hp: 46,   radius: 1.15, score: 260,  salvage: 12,  contact: 12,  threat: 0.35, lockable: true  },
  swarmer:     { hp: 9,    radius: 1.5,  score: 45,   salvage: 2,   contact: 9,   threat: 0.3,  lockable: false },
  lurker:      { hp: 78,   radius: 3.6,  score: 420,  salvage: 22,  contact: 26,  threat: 0.7,  lockable: true  },
  turret:      { hp: 92,   radius: 3.0,  score: 300,  salvage: 20,  contact: 0,   threat: 0.55, lockable: true  },
  hunter:      { hp: 150,  radius: 3.4,  score: 650,  salvage: 38,  contact: 20,  threat: 0.85, lockable: true  },
  sentinel:    { hp: 210,  radius: 3.8,  score: 900,  salvage: 55,  contact: 24,  threat: 0.9,  lockable: true  },
  dreadnought: { hp: 2600, radius: 15,   score: 9000, salvage: 600, contact: 34,  threat: 1.0,  lockable: true  },
};

/** Perception and steering shared by every mechanical hunter. */
export const DRONE = {
  speed: 15.5,
  accel: 26,
  sightRange: 78,
  /** Detection is much shorter when the player runs dark. */
  darkRange: 26,
  hearRange: 46,
  fireRange: 62,
  fireInterval: 1.4,
  bulletSpeed: 62,
  bulletDamage: 6,
} as const;

/** A mine that comes looking once it hears you. */
export const SEEKER = {
  wakeRange: 46,
  speed: 12,
  accel: 15,
  triggerRange: 6.5,
  /** It gives up and re-moors after this long without contact. */
  patience: 9,
} as const;

export const MINE = {
  triggerRange: 7.5,
  armTime: 0.5,
  blastRadius: 15,
  damage: 30,
} as const;

/** Anchored gun emplacement. Cannot chase, so it hits hard and covers ground. */
export const TURRET = {
  range: 96,
  /** Radians per second the barrel can traverse. */
  traverse: 1.5,
  /** Shots in a burst, and the gap between them. */
  burst: 3,
  burstGap: 0.13,
  reload: 2.1,
  bulletSpeed: 76,
  bulletDamage: 8,
  /** Half-angle of the arc it can cover from its mount. */
  arc: (105 * Math.PI) / 180,
} as const;

/** Enemy submarine: pings, closes, salvos torpedoes, breaks off when hurt. */
export const HUNTER = {
  speed: 20,
  accel: 24,
  sightRange: 130,
  darkRange: 62,
  /** It runs its own active sonar, which lights the sub up regardless of the lamp. */
  pingInterval: 5.5,
  pingRange: 190,
  standoff: 62,
  fireRange: 118,
  salvo: 2,
  salvoGap: 0.55,
  reload: 4.2,
  torpedoDamage: 26,
  torpedoSpeed: 40,
  torpedoTurn: 1.5,
  torpedoLife: 6,
  torpedoBlast: 9,
  /** Below this fraction of health it disengages and tries to heal. */
  breakOff: 0.3,
  healRate: 6,
} as const;

/** Shoal fish. Individually trivial, collectively a wall of teeth. */
export const SWARMER = {
  speed: 26,
  accel: 60,
  noticeRange: 72,
  /** Flock cohesion / separation weights. */
  cohere: 1.1,
  separate: 3.2,
  separateRange: 4.5,
  /** Seconds between darting lunges. */
  lungeGap: 1.9,
  lungeSpeed: 46,
  /** How long the shoal keeps hunting after losing you. */
  patience: 7,
} as const;

/** Ambush predator: sits dark in a hole, shows a lure, then rushes. */
export const LURKER = {
  lureRange: 58,
  strikeRange: 26,
  rushSpeed: 44,
  rushAccel: 130,
  rushTime: 1.1,
  recover: 2.6,
  /** Damage of the bite itself, applied once per rush. */
  bite: 24,
} as const;

/** Armoured drone with a rotating shield plate — hit it from behind. */
export const SENTINEL = {
  speed: 13,
  accel: 20,
  sightRange: 105,
  fireRange: 78,
  fireInterval: 1.05,
  bulletSpeed: 70,
  bulletDamage: 9,
  /** Half-angle of the armoured arc. */
  plateArc: (78 * Math.PI) / 180,
  /** Damage multiplier for shots landing on the plate. */
  plateSoak: 0.12,
  /** Radians per second the plate tracks the threat. */
  plateTrack: 1.25,
} as const;

/** Zone boss: an enemy capital submarine with destructible weapon pods. */
export const DREAD = {
  speed: 9,
  accel: 9,
  /** Weapon pods, each destroyed separately, disabling one attack. */
  gunRange: 130,
  gunInterval: 0.22,
  gunBurst: 5,
  gunReload: 2.6,
  bulletSpeed: 74,
  bulletDamage: 9,
  torpedoInterval: 5.0,
  torpedoDamage: 34,
  mineInterval: 6.5,
  /** Charging ram attack once both pods are gone. */
  ramSpeed: 40,
  ramDamage: 46,
} as const;

// ---------------------------------------------------------------------------
// Pickups and loot
// ---------------------------------------------------------------------------

export type PickupType =
  | 'salvage' | 'repair' | 'shield' | 'energy' | 'nitro' | 'torpedo'
  | 'charge' | 'overdrive' | 'aegis' | 'life' | 'module'
  /**
   * A mineral shard, broken out of a deposit. Which SEAM it came from lives on
   * the pickup rather than in the type, because eight more `PickupType`s would
   * mean eight more entries in every drop table, every save list and every
   * switch that handles them — for a distinction that is one number.
   */
  | 'ore';

export interface PickupDef {
  label: string;
  /** HUD/glow colour. */
  color: [number, number, number];
  /** Default magnitude — hull points, shield points, ammo rounds, salvage. */
  amount: number;
}

export const PICKUP_DEF: Record<PickupType, PickupDef> = {
  salvage:   { label: 'SALVAGE',       color: [1.0, 0.78, 0.28], amount: 25 },
  repair:    { label: 'REPAIR CELL',   color: [0.35, 1.0, 0.52], amount: 34 },
  shield:    { label: 'SHIELD CELL',   color: [0.35, 0.72, 1.0], amount: 999 },
  energy:    { label: 'POWER CELL',    color: [1.0, 0.86, 0.42], amount: 999 },
  nitro:     { label: 'NITRO CANISTER',color: [1.0, 0.42, 0.86], amount: NITRO.refill },
  torpedo:   { label: 'TORPEDOES',     color: [0.45, 0.9, 1.0],  amount: 3 },
  charge:    { label: 'DEPTH CHARGES', color: [1.0, 0.6, 0.3],   amount: 2 },
  overdrive: { label: 'OVERDRIVE',     color: [1.0, 0.35, 0.35], amount: POWER.overdrive.time },
  aegis:     { label: 'AEGIS FIELD',   color: [0.7, 0.95, 1.0],  amount: POWER.aegis.time },
  life:      { label: 'SPARE HULL',    color: [1.0, 1.0, 1.0],   amount: 1 },
  module:    { label: 'SALVAGED MODULE', color: [1.0, 0.55, 0.22], amount: 220 },
  // Label and colour are overridden per shard from the seam it came from.
  ore:       { label: 'ORE',            color: [0.8, 0.85, 0.95], amount: 20 },
};

export interface DropChance {
  type: PickupType;
  /** 0..1 probability. Rolled independently per entry. */
  chance: number;
  amount?: number;
}

/**
 * What each enemy leaves behind. Mechanical kills reliably pay salvage; the
 * loud powerups are rare enough to feel like an event and common enough that a
 * long fight always produces one.
 */
export const DROPS: Record<EnemyType, DropChance[]> = {
  mine:        [{ type: 'salvage', chance: 0.35, amount: 10 }],
  seeker:      [{ type: 'salvage', chance: 0.55, amount: 14 }, { type: 'energy', chance: 0.10 }],
  drone:       [{ type: 'salvage', chance: 0.7,  amount: 16 }, { type: 'repair', chance: 0.12 }, { type: 'nitro', chance: 0.10 }],
  jelly:       [{ type: 'energy',  chance: 0.10 }],
  bloom:       [{ type: 'salvage', chance: 0.4,  amount: 14 }, { type: 'energy', chance: 0.22 }],
  swarmer:     [{ type: 'salvage', chance: 0.14, amount: 6 }],
  lurker:      [{ type: 'salvage', chance: 0.9,  amount: 30 }, { type: 'repair', chance: 0.3 }, { type: 'torpedo', chance: 0.25 }],
  turret:      [
    { type: 'salvage', chance: 1.0, amount: 26 }, { type: 'torpedo', chance: 0.3 },
    { type: 'shield', chance: 0.15 }, { type: 'charge', chance: 0.12 },
  ],
  hunter:      [
    { type: 'salvage', chance: 1.0, amount: 46 }, { type: 'torpedo', chance: 0.55 },
    { type: 'repair', chance: 0.3 }, { type: 'overdrive', chance: 0.14 },
    { type: 'charge', chance: 0.2 }, { type: 'module', chance: 0.05 },
  ],
  sentinel:    [
    { type: 'salvage', chance: 1.0, amount: 64 }, { type: 'shield', chance: 0.5 },
    { type: 'nitro', chance: 0.35 }, { type: 'overdrive', chance: 0.2 },
    { type: 'charge', chance: 0.25 }, { type: 'module', chance: 0.1 },
    { type: 'aegis', chance: 0.08 },
  ],
  dreadnought: [
    { type: 'salvage', chance: 1.0, amount: 300 },
    { type: 'life', chance: 1.0 },
    { type: 'repair', chance: 1.0 },
    { type: 'torpedo', chance: 1.0, amount: 8 },
    { type: 'charge', chance: 1.0, amount: 4 },
    { type: 'aegis', chance: 1.0 },
  ],
};

/** How a loose pickup behaves once it has been spat out of a wreck. */
export const LOOT = {
  /** Initial scatter speed. */
  burst: 9,
  drag: 1.5,
  /**
   * Loot SINKS, and comes to rest on the floor.
   *
   * It used to rise, at 2.2, on the reasoning that loot which sinks disappears
   * into the rock. That problem is real and it is already solved twice over,
   * ten lines below: a push-out along the density gradient catches anything
   * that reaches the seabed, exactly as it does for cargo. The buoyancy was a
   * second guard against a failure the first one already covers, and what it
   * bought instead was a water column filling up with canisters that drift
   * upward and never land — reported as "they never really fall on the ocean
   * floor, they tend to float".
   *
   * Falling is also what a player expects of a dropped object, and it puts the
   * spoils of a fight where the fight was rather than above it.
   */
  sink: 3.4,
  /**
   * How close to the rock counts as landed. Wider than the push-out band, for
   * the same reason cargo needs `restBand`: a piece of loot that only rests
   * while inside the band it is being shoved out of never rests at all — it
   * sinks a centimetre, gets pushed back, and buzzes at frame rate forever.
   */
  restBand: 2.2,
  /** Seconds before it fades out; authored pickups never expire. */
  life: 34,
  pickupRadius: 5.6,
} as const;

/**
 * Cargo. A pickup with `mass > 0` is not a pickup at all — it is an object in
 * the water that has to be dragged home.
 *
 * The load is expressed as a *factor* on accelerations rather than by
 * re-authoring `SUB` as forces. Every term in `Player.update` is already an
 * acceleration, so dividing thrust and drag by the load gives correct F/m
 * behaviour and leaves terminal speed — thrust/drag — invariant. That matters
 * more than it sounds: `camera.ts`'s `maxSpeed`, the attitude normalisers in
 * `player.ts` and every dps figure in the balance table are all calibrated
 * against the current terminal speed, and none of them have to move.
 *
 * What *does* cost top speed is `towDrag`, and it should: a crate on a cable
 * has its own cross-section in the flow. Drag depends on shape, not on mass,
 * so this term is deliberately not divided out by the extra weight.
 */
export const CARGO = {
  /** The empty boat, in the tonnes cargo is measured in. Only the ratio matters. */
  dryMass: 1.0,
  /**
   * Downward acceleration per tonne. Neutral trim lands at
   * `buoyancy * dryMass / sinkAccel` ≈ 0.6 t — under that a load still drifts
   * up, over it the boat sinks when you stop pushing. That is the whole tell:
   * you feel what you are carrying without reading a gauge.
   */
  sinkAccel: 5.2,
  /** Extra drag coefficient per tonne, on top of the hull's own. */
  towDrag: 0.55,
  /** How fast a dropped crate sinks on its own, and how much water slows it. */
  crateSink: 3.4,
  crateDrag: 1.1,
  /** Collision radius of a crate against rock. */
  crateRadius: 1.5,
  /** Below this speed a crate near the seabed is pinned outright. */
  restSpeed: 1.2,
  /**
   * Extra margin above `crateRadius` within which a slow crate counts as
   * settled. Without a gap between "resting" and "fouled in rock" the two tests
   * never agree: a crate settles just clear of the surface that pushed it
   * there, so a pin evaluated only inside the push-out band never fires, and
   * the crate oscillates at frame rate forever.
   */
  restBand: 1.2,
  /**
   * What a station pays over a load's face value for bringing it in physically.
   *
   * This premium is the whole reason to haul rather than to shoot the thing and
   * scoop the pieces, and it has to be big enough to be felt on the first crate
   * — 35 % on a 90-unit pod is 32 salvage, about a third of the cheapest
   * upgrade. Much smaller and towing reads as a chore with a rounding error
   * attached; much larger and it dwarfs combat income and the game stops being
   * about the descent.
   */
  deliveryBonus: 1.35,
  /**
   * Extra premium per kilometre the load has actually been moved, on top of
   * `deliveryBonus`, capped at `haulCap` km.
   *
   * Without this the whole heavy-salvage design is worth nothing. Stations sit
   * at 5400, 6160, 6820 and 7120, so every deep load is within 700 m of one —
   * at a flat rate the payout for dragging a 4.6 t mass 2.5 km up out of the
   * abyss is identical to the payout for shuffling it to the next checkpoint,
   * and the climb that is the best thing in the game earns nothing.
   */
  haulBonus: 0.30,
  haulCap: 5,
} as const;

/**
 * The grapple. A hook on a line: fired, caught, winched, cut.
 *
 * The line is a rope and not a spring — it does nothing at all until it is
 * taut, which is what makes a load swing through a tunnel rather than
 * oscillate on the end of a bungee. One positional constraint, one iteration,
 * solved once per frame.
 */
export const GRAPPLE = {
  /** How far the hook will reach before it gives up and reels back. */
  /**
   * How far the grab reaches — WHICH IS NOW THE ARM'S REACH.
   *
   * 34 m was a thrown hook on a long cable: aim, fire, watch it arc, winch the
   * load home. The MK II has an actual manipulator, and a boat that reaches
   * out and takes a thing is a different verb from one that harpoons it at
   * thirty metres. So the range is the length of the arm.
   *
   * 8.5 is the claw's own stretch plus a little: the grip lands about 8.1 m
   * below the axis at full extension, which is exactly `fairleadDrop` (1.58)
   * plus `minRest` (6.5) — so a load at its resting tether sits IN the claw
   * rather than dangling below it.
   *
   * THIS IS A GAMEPLAY CHANGE, not a visual one. Salvage now has to be flown
   * to rather than fished for, and anything previously grabbable from four
   * boat-lengths away now needs approaching.
   */
  range: 8.5,
  /**
   * Deployment speed. Deliberately slow — this is a salvage winch lowering a
   * hook off the belly, not a harpoon gun. A fast hook fired from the nose read
   * as a weapon and made recovery feel like shooting, which is the opposite of
   * the fantasy: the interesting part is the weight on the way home, not the
   * grab.
   */
  speed: 21,
  /**
   * Downward acceleration on the hook while it is out, m/s^2. It is a lump of
   * steel on a cable, so it falls as it goes, and a load below the boat can be
   * reached by simply paying out line rather than by aiming at it.
   */
  sink: 16,
  /** Radius the hook catches within. */
  catchRadius: 3.2,
  /**
   * Shortest the winch will pull to. The load rides just off the tail — close
   * enough to stop it pendulum-ing through a corridor, far enough that it is
   * not inside the hull.
   *
   * There is deliberately no *maximum* other than `range`: the tether starts at
   * however much line was actually paid out to reach the thing. Capping it at a
   * shorter "rest" length meant any catch past that cap was instantly over-
   * stretched and parted on the frame it was made — two thirds of the hook's
   * advertised reach was a trap, and the HUD prompted for it.
   */
  minRest: 6.5,
  /** Metres per second of line taken in or paid out. */
  winch: 7.5,
  /**
   * Held for less than this, GRAB cuts the line; held longer, it winches in.
   * One button, because every pad face button, trigger, bumper and stick click
   * is already bound.
   */
  tapTime: 0.22,
  /**
   * Stretch past the tether, in metres, at which the line parts. Snapping is a
   * consequence of flying into something with a load on, not a random event.
   */
  snap: 3.0,
} as const;

// ---------------------------------------------------------------------------
// Progression
// ---------------------------------------------------------------------------

export type UpgradeId =
  | 'hull' | 'shield' | 'engine' | 'cannon' | 'torpedo' | 'sonar' | 'lamp'
  | 'nitro' | 'ablative' | 'warding' | 'winch';

export interface UpgradeDef {
  id: UpgradeId;
  name: string;
  /** One line the player reads in the shop, per level. */
  blurb: string[];
  /** Salvage price of each successive level. Length = number of levels. */
  costs: number[];
}

/**
 * The bays the refit list is grouped into.
 *
 * ELEVEN ROWS IS NOT A LIST, IT IS A WALL. Flat, the shop asked the player to
 * hold every module in the game in their head at once to answer "what do I
 * want next" — and the two rows that matter on any given visit were somewhere
 * in the middle of nine that did not. Four bays is a question with four
 * answers, and the answer picks the four-or-fewer rows worth reading.
 *
 * HERE RATHER THAN IN THE MENU because which bay a module belongs to is a fact
 * about the module, the same way its price is. The menu decides how a bay is
 * drawn; it does not get to decide that the winch is deck gear.
 *
 * `check-shop.mjs` asserts every id in `UPGRADES` appears in exactly one bay,
 * which is the failure this shape invites: add a twelfth module and the only
 * symptom of forgetting this table is that it cannot be bought.
 */
export interface UpgradeBay {
  id: string;
  name: string;
  /** What the bay is for, in the words a player would use. */
  blurb: string;
  members: readonly UpgradeId[];
}

export const UPGRADE_BAYS: readonly UpgradeBay[] = [
  { id: 'bay-arm', name: 'ARMAMENT', blurb: 'Guns and fish',
    members: ['cannon', 'torpedo'] },
  { id: 'bay-def', name: 'SURVIVABILITY', blurb: 'Plate, fields and what soaks a hit',
    members: ['hull', 'shield', 'ablative', 'warding'] },
  { id: 'bay-drv', name: 'PROPULSION', blurb: 'Speed, and the burner behind it',
    members: ['engine', 'nitro'] },
  { id: 'bay-sys', name: 'SYSTEMS & DECK GEAR', blurb: 'Seeing, lighting and lifting',
    members: ['sonar', 'lamp', 'winch'] },
];

export const UPGRADES: UpgradeDef[] = [
  {
    id: 'hull', name: 'HULL PLATING',
    blurb: ['+30 hull', '+30 hull', '+30 hull', '+40 hull, impacts hurt less'],
    costs: [120, 260, 480, 820],
  },
  {
    id: 'shield', name: 'DEFLECTOR',
    blurb: ['Shields online', 'Stronger, recharges sooner', 'Stronger again', 'Heavy deflector'],
    costs: [150, 300, 560, 900],
  },
  {
    id: 'engine', name: 'DRIVE',
    blurb: [
      'Faster, bigger nitro tank', 'Faster again',
      'Far bigger tank, burns slower, refills fast',
      'Higher top speed',
      'Much higher top speed',
    ],
    costs: [140, 320, 640, 980, 1500],
  },
  {
    id: 'cannon', name: 'CANNON',
    blurb: [
      'Harder hitting, faster', 'Twin barrels', 'Three-way spread', 'Five-way storm',
      'Tighter, harder, faster', 'Seven barrels',
    ],
    costs: [130, 300, 620, 1000, 1450, 2100],
  },
  {
    id: 'torpedo', name: 'TORPEDO BAY',
    blurb: ['+4 capacity, bigger blast', '+4 capacity, more damage', 'Depth charges unlocked'],
    costs: [180, 380, 700],
  },
  {
    id: 'sonar', name: 'SONAR ARRAY',
    blurb: ['Longer range, cheaper ping', 'Ping becomes a shockwave', 'Wider, harder shockwave'],
    costs: [160, 340, 660],
  },
  {
    id: 'lamp', name: 'LAMP ARRAY',
    blurb: ['Brighter, longer reach', 'Wide-field floodlight'],
    costs: [110, 280],
  },
  /**
   * Lifting gear, split off the drive for the same reason the burner was: it
   * was two words at the end of a DRIVE blurb, so the system a player spends
   * whole minutes wrestling with had no dial of its own — and no way to learn
   * that it HAD a rating at all.
   *
   * This one carries a hard number as well as a soft one. `liftCap` is what a
   * crate must weigh less than before the hook will take it: a limit that can
   * be stated, refused against, and then bought past. The drag multiplier
   * rides with it, so a boat that can lift a thing is also a boat that can
   * move once it has.
   */
  {
    id: 'winch', name: 'WINCH GEAR',
    blurb: [
      'Lifts 2.4 t, hauls steadier',
      'Lifts 3.6 t, less drag under load',
      'Lifts 5.0 t, heavy tackle',
      'Lifts 7.0 t, a load barely slows you',
    ],
    costs: [150, 330, 680, 1120],
  },
  // The burner, split off the drive. It was three lines buried in DRIVE, which
  // meant the one system a player consciously *uses* had no dial of its own.
  {
    id: 'nitro', name: 'BURNER',
    blurb: [
      'Bigger tank', 'Burns longer, refills sooner', 'Harder push',
      'Much harder push, thirstier for it',
    ],
    costs: [160, 340, 700, 1150],
  },
  // Two defences that are NOT the deflector, and not each other. The deflector
  // is a pool that absorbs anything; these are resistances to one kind of
  // thing, and they read differently on the hull because they are different.
  {
    id: 'ablative', name: 'ABLATIVE PLATE',
    blurb: ['Scrapes hurt less', 'Rams shrug off rock', 'Collisions barely register'],
    costs: [190, 420, 820],
  },
  {
    id: 'warding', name: 'WARD FIELD',
    blurb: ['Blasts hurt less', 'Torpedoes lose their bite', 'Stands in a minefield'],
    costs: [230, 500, 950],
  },
];

/**
 * One tint per shoal. A reef should read as several species sharing the water,
 * not as one cloud of identical fish, so every shoal group picks a hue from
 * here and every fish in it varies around that hue slightly.
 */
export const SHOAL_TINTS: [number, number, number][] = [
  [0.42, 0.46, 0.44],   // silver
  [0.50, 0.45, 0.36],   // brass
  [0.38, 0.48, 0.46],   // verdigris
  [0.50, 0.42, 0.46],   // rose steel
  [0.44, 0.49, 0.38],   // olive steel
  [0.40, 0.43, 0.52],   // blue steel
  [0.50, 0.48, 0.42],   // sand
  [0.38, 0.46, 0.52],   // sky steel
];

/**
 * Per-species body flex: [model half-length, wave amplitude, turn curvature].
 * The wave is the swimming stroke; the curvature is how far the body arcs
 * through a turn, which is the difference between an eel and a length of pipe.
 */
export const FISH_FLEX: [number, number, number][] = [
  [1.42, 0.15, 0.55],   // darter  — a flick of the tail
  [1.18, 0.10, 0.30],   // lantern — deep bodied, barely bends
  [2.55, 0.62, 1.90],   // ribbon  — an eel; almost all of it moves
  [1.10, 0.09, 0.22],   // fan     — a disc, it sculls rather than undulates
];

/**
 * Weighted species draw. Most of a shoal is the sleek darter — that silhouette
 * is what reads as "fish" at distance, and the rest are seasoning rather than
 * equal partners.
 */
export const FISH_MIX = [0, 0, 0, 0, 0, 1, 2, 3];

/** The big decorative shoals — see `shoal.ts`. */
export const BAIT = {
  /** Stop simulating a ball past this distance from the camera. */
  cullRange: 175,
  /** How far into Z the ball extends, as a fraction of its radius. Kept
   *  modest: Z is toward the camera, so depth is also apparent size. */
  depthScale: 0.42,
  /** The sub parts the ball inside this radius. */
  partRadius: 16,
} as const;

/** Score awarded for things that are not kills. */
export const SCORE = {
  discovery: 250,
  station: 400,
  salvagePerUnit: 12,
} as const;

// ---------------------------------------------------------------------------
// time of day
// ---------------------------------------------------------------------------

/**
 * Where the sun is, and what colour it is when it gets here.
 *
 * The sun used to be one hard-coded vector pointing very nearly straight down,
 * which is midday and only midday. It matters more than it used to: the sun
 * now casts the scene's ambient shadow, so its ELEVATION is the difference
 * between shadows pooled directly under things and shadows raking across the
 * sea floor. A time of day is therefore a lighting decision, not just a tint.
 */
export interface SunPreset {
  id: string;
  name: string;
  /**
   * Degrees above the horizon, in AIR. This is refracted on the way in — see
   * `sunUnderwater` — so it is not the angle the game will actually use.
   */
  elevation: number;
  /**
   * Degrees around the vertical. 0 leans the light along +X, 180 along -X, so
   * this is what swings the shadows from one side of the world to the other
   * across a day.
   */
  azimuth: number;
  /** Colour of the light ABOVE the surface, before the water takes the red. */
  color: [number, number, number];
  /** Artistic weight on top of the physics, mostly to keep night playable. */
  gain: number;
}

/**
 * A day, in order. Ordered because the menu steps through it, and stepping
 * through a day should feel like a day passing rather than a shuffled list.
 */
export const SUN_PRESETS: readonly SunPreset[] = [
  { id: 'dawn',    name: 'DAWN',        elevation: -4, azimuth: 0,   color: [0.44, 0.46, 0.78], gain: 0.55 },
  { id: 'sunrise', name: 'SUNRISE',     elevation: 2,  azimuth: 0,   color: [1.00, 0.60, 0.34], gain: 0.85 },
  { id: 'morning', name: 'MORNING',     elevation: 30, azimuth: 12,  color: [1.00, 0.91, 0.78], gain: 1.00 },
  { id: 'noon',    name: 'NOON',        elevation: 72, azimuth: 48,  color: [1.00, 0.99, 0.96], gain: 1.00 },
  { id: 'day',     name: 'AFTERNOON',   elevation: 46, azimuth: 168, color: [1.00, 0.96, 0.86], gain: 1.00 },
  { id: 'golden',  name: 'GOLDEN HOUR', elevation: 8,  azimuth: 180, color: [1.00, 0.71, 0.38], gain: 0.95 },
  { id: 'evening', name: 'EVENING',     elevation: 3,  azimuth: 180, color: [1.00, 0.50, 0.28], gain: 0.80 },
  { id: 'dusk',    name: 'DUSK',        elevation: -5, azimuth: 180, color: [0.46, 0.40, 0.70], gain: 0.50 },
  // The moon, honestly labelled. It is a light source that IS above the
  // horizon at night, so it gets a real elevation rather than a sun pushed
  // below the world and fudged back up with gain.
  { id: 'night',   name: 'NIGHT',       elevation: 52, azimuth: 150, color: [0.34, 0.46, 0.78], gain: 0.10 },
] as const;

export const DEFAULT_SUN = 'day';

/** Refraction index of sea water against air. */
const WATER_IOR = 1.34;

export interface SunState {
  /** Unit vector pointing DOWN-current, the direction light travels. */
  dx: number; dy: number; dz: number;
  /** The same sun BEFORE the surface bends it. For the sky, and only the sky. */
  ax: number; ay: number; az: number;
  /** Fraction of the above-surface light that makes it in, 0..1. */
  transmit: number;
  /** How much further light travels to reach a given depth, >= 1. */
  pathScale: number;
  color: [number, number, number];
  /** The preset's above-water brightness, with no surface term in it. */
  gain: number;
}

/**
 * A preset, refracted through the surface.
 *
 * SNELL'S LAW IS THE WHOLE POINT OF DOING THIS PROPERLY. Light entering water
 * bends toward the vertical, and because the index is 1.34 the steepest it can
 * ever arrive is 48.5 degrees off vertical — Snell's window. So a sun sitting
 * on the horizon does NOT give horizontal underwater light; it gives light at
 * 48.5 degrees, and no time of day can do better. That single fact is what
 * keeps every preset physically reachable instead of letting sunrise light the
 * sea floor sideways like a stage lamp.
 *
 * Two consequences come free once the angle is right:
 *
 * FRESNEL. A low sun mostly reflects OFF the surface rather than entering it —
 * transmission runs from 0.98 overhead to nearly nothing at grazing. This is
 * the real reason it goes dark underwater long before sunset looks dim from a
 * boat, and it means the presets need no hand-authored brightness curve.
 *
 * PATH LENGTH. Light arriving at an angle travels further through the water to
 * reach the same depth, so it is extinguished more. At Snell's limit that is
 * 1.5x the vertical path, which noticeably deepens the blue at either end of
 * the day.
 */
export function sunUnderwater(p: SunPreset): SunState {
  const rad = Math.PI / 180;
  // Zenith angle in air. Clamped at the horizon: below it there is no direct
  // beam to refract, and the residual light is sky, which the floor covers.
  const za = Math.min(90 - p.elevation, 90) * rad;
  const sinT = Math.min(Math.sin(za) / WATER_IOR, 1);
  const zw = Math.asin(sinT);
  const cosA = Math.cos(za), cosW = Math.cos(zw);

  // Fresnel for unpolarised light, both components, averaged.
  const rs = (cosA - WATER_IOR * cosW) / (cosA + WATER_IOR * cosW);
  const rp = (WATER_IOR * cosA - cosW) / (WATER_IOR * cosA + cosW);
  // The floor is diffuse skylight. The sky is a hemisphere, not a point, so
  // light keeps arriving from overhead after the sun itself stops getting in —
  // without this, dusk is mathematically correct and unplayably black.
  const transmit = Math.max(1 - (rs * rs + rp * rp) / 2, 0.05) * p.gain;

  const az = p.azimuth * rad;
  // THE SUN IN AIR, WHICH IS A DIFFERENT DIRECTION FROM THE SUN IN WATER.
  //
  // Everything below `dx/dy/dz` is the REFRACTED beam: `sinT` is Snell's law
  // applied at the surface and `cosW` is the angle inside the water, which is
  // exactly right for lighting a seabed and exactly wrong for drawing a sky.
  // The sky shader was being handed it anyway, so at Golden Hour — 8 degrees of
  // elevation — refraction lifted the sun to FORTY-TWO degrees and the preset
  // rendered as a blue midday. Snell's window compresses the whole hemisphere
  // into a 48.6 degree cone; that is a fact about being underwater, and the air
  // above the surface is not.
  const sinA = Math.sin(za);
  return {
    dx: sinT * Math.cos(az),
    dy: -cosW,
    dz: sinT * Math.sin(az),
    ax: sinA * Math.cos(az),
    ay: -cosA,
    az: sinA * Math.sin(az),
    transmit,
    pathScale: 1 / Math.max(cosW, 0.4),
    color: p.color,
    // The preset's own brightness, UNTOUCHED by the surface. `transmit` already
    // has it folded in, but transmit is about how much light gets INTO the
    // water — it carries a Fresnel term that is nearly total at a low sun — and
    // the sky above the water has no business being dimmed by that.
    gain: p.gain,
  };
}
